import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useClientMembers,
  useCurrentClient,
} from "@/features/clients/hooks/use-clients";
import type {
  CalendarEvent,
  Schedule,
  TimeOff,
} from "@/features/calendar/types";
import type { Reminder } from "@/features/reminders/types";
import { isSameDay } from "date-fns";
import { useMemo } from "react";
import { DayColumn } from "./column-day";
import { TimeColumn } from "./column-time";
import { useCalendarView } from "../hooks/use-calendar-view";
import { useDragDrop } from "../hooks/use-drag-drop";
import { useWorkHoursScroll } from "../hooks/use-work-hours-scroll";

interface CalendarDayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  reminders?: Reminder[];
  schedules?: Schedule[];
  timeOffs?: TimeOff[];
  onSlotClick: (startTime: number, endTime: number) => void;
  onEventClick: (event: CalendarEvent) => void;
  selectedMembers: string[];
  selectedServices: string[];
}

export function CalendarDayView({
  currentDate,
  events,
  reminders = [],
  schedules = [],
  timeOffs = [],
  onSlotClick,
  onEventClick,
  selectedMembers,
  selectedServices,
}: CalendarDayViewProps) {
  const { timeSlots, filterEvents } = useCalendarView();
  const { scrollAreaRef } = useWorkHoursScroll(schedules);
  const clientId = useCurrentClient()?.client?.id || "";
  const { data: members = [] } = useClientMembers(clientId);
  const {
    draggedEvent,
    dragOverSlot,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragDrop();

  // Filter events for the current day
  const filteredEvents = useMemo(() => {
    return filterEvents(
      events,
      selectedMembers,
      selectedServices,
      members,
      (event) => {
        const eventDate = new Date(event.start_time);
        return (
          eventDate.getFullYear() === currentDate.getFullYear() &&
          eventDate.getMonth() === currentDate.getMonth() &&
          eventDate.getDate() === currentDate.getDate()
        );
      },
    );
  }, [
    events,
    currentDate,
    selectedMembers,
    selectedServices,
    members,
    filterEvents,
  ]);

  return (
    <div className="flex h-full flex-col">
      {/* Calendar grid */}
      <ScrollArea className="flex-1" ref={scrollAreaRef}>
        <div className="relative grid grid-cols-[70px_1fr]">
          {/* Time labels */}
          <TimeColumn timeSlots={timeSlots} />

          {/* Day column */}
          <DayColumn
            className="relative border-r"
            day={currentDate}
            dayIndex={0}
            events={filteredEvents}
            reminders={reminders.filter((reminder) =>
              isSameDay(new Date(reminder.scheduled_at), currentDate),
            )}
            schedules={schedules}
            timeOffs={timeOffs}
            selectedMembers={selectedMembers}
            onEventClick={onEventClick}
            onSlotClick={onSlotClick}
            draggedEvent={draggedEvent}
            dragOverSlot={dragOverSlot}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
